class AnnouncementBarCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-announcement-track]');
    if (!this.track) return;

    this.slides = [...this.querySelectorAll('[data-announcement-slide]')];
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.mobileMedia = window.matchMedia('(max-width: 749px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayTimer = null;
    this.currentIndex = 0;

    this.prevButton?.addEventListener('click', () => this.navigate(-1));
    this.nextButton?.addEventListener('click', () => this.navigate(1));

    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.track);

    this.mobileMedia.addEventListener('change', () => {
      this.currentIndex = 0;
      this.refresh();
      this.scrollToIndex(0, false);
    });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.setupAutoplay());
    this.addEventListener('focusin', (event) => {
      if (this.contains(event.target)) this.pauseAutoplay();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.setupAutoplay();
    });

    this.reducedMotion.addEventListener('change', () => this.setupAutoplay());

    this.refresh();
    this.setupThemeEditor();
  }

  setupThemeEditor() {
    if (!Shopify?.designMode) return;

    this.addEventListener('shopify:block:select', () => this.refresh());
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.contains(this)) this.refresh();
    });
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    this.resizeObserver?.disconnect();
  }

  get isMobile() {
    return this.mobileMedia.matches;
  }

  get visibleCount() {
    if (this.isMobile) return 1;
    return Math.min(3, this.slides.length);
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - this.visibleCount);
  }

  get slideStep() {
    if (this.slides.length < 2) return this.track.clientWidth;
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  }

  refresh() {
    const visible = this.visibleCount;
    const widthPercent = 100 / visible;

    this.slides.forEach((slide) => {
      slide.style.flexBasis = `${widthPercent}%`;
      slide.style.width = `${widthPercent}%`;
    });

    this.classList.toggle('is-scrollable', this.slides.length > this.visibleCount);
    this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
    this.updateControls();
    this.updateAriaHidden();
    this.setupAutoplay();
  }

  scrollToIndex(index, smooth = true) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    const left = this.slides[this.currentIndex]?.offsetLeft ?? 0;

    this.track.scrollTo({
      left,
      behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'auto',
    });

    this.updateAriaHidden();
    this.updateControls();
  }

  navigate(direction, fromAutoplay = false) {
    if (!fromAutoplay) this.pauseAutoplay();

    const next = this.currentIndex + direction;
    if (next < 0) {
      this.scrollToIndex(this.maxIndex);
    } else if (next > this.maxIndex) {
      this.scrollToIndex(0);
    } else {
      this.scrollToIndex(next);
    }

    if (!fromAutoplay) this.setupAutoplay();
  }

  onScroll() {
    if (!this.slideStep) return;

    const index = Math.round(this.track.scrollLeft / this.slideStep);
    const clampedIndex = Math.max(0, Math.min(index, this.maxIndex));

    if (clampedIndex !== this.currentIndex) {
      this.currentIndex = clampedIndex;
      this.updateAriaHidden();
      this.updateControls();
    }
  }

  updateAriaHidden() {
    this.slides.forEach((slide, index) => {
      const inView = index >= this.currentIndex && index < this.currentIndex + this.visibleCount;
      slide.setAttribute('aria-hidden', inView ? 'false' : 'true');

      slide.querySelectorAll('a').forEach((link) => {
        link.tabIndex = inView ? 0 : -1;
      });
    });
  }

  updateControls() {
    const needsNav = this.slides.length > this.visibleCount;

    [this.prevButton, this.nextButton].forEach((button) => {
      if (!button) return;
      button.hidden = !needsNav || this.isMobile;
    });
  }

  setupAutoplay() {
    this.pauseAutoplay();

    const autoplayEnabled = this.dataset.autoplayMobile !== 'false';
    if (!this.isMobile || !autoplayEnabled || this.reducedMotion.matches || this.slides.length < 2) return;

    this.autoplayTimer = window.setInterval(() => this.navigate(1, true), this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

customElements.define('announcement-bar-carousel', AnnouncementBarCarousel);
